import { useToast } from '@chakra-ui/react';
import {
  GetListingReportShareLinksDocument,
  useGetListingReportShareLinksQuery,
  useRevokeListingReportShareLinkMutation,
  useSendListingReportShareLinkMutation,
} from '@client/graphql/__generated__/main-operations';
import { useState } from 'react';
import { useLocation } from 'react-router';
import { openInNewTab } from '~/apps/consumer/utils/browser.utils';
import { ShareModal } from '../../ShareModal/ShareModal';
import { useListingPageContext } from './ListingPageContext';

interface ShareListingReportWithClientModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ShareListingReportWithClientModal = ({
  isOpen,
  onClose,
}: ShareListingReportWithClientModalProps) => {
  const location = useLocation();
  const { listing, propertyDetails } = useListingPageContext();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const { data: shareLinksData } = useGetListingReportShareLinksQuery({
    variables: {
      input: {
        listingId: listing?.id,
        propertyDetailsId: propertyDetails?.id,
      },
    },
    skip: !listing?.id && !propertyDetails?.id,
  });

  const [sendListingReportShareLink] = useSendListingReportShareLinkMutation();
  const [revokeListingReportShareLink] =
    useRevokeListingReportShareLinkMutation();
  const shareLinks = shareLinksData?.getListingReportShareLinks || [];

  const revokeShareLink = (token: string) => {
    void revokeListingReportShareLink({
      variables: {
        input: {
          token,
          listingId: listing?.id,
          propertyDetailsId: propertyDetails?.id,
        },
      },
      refetchQueries: [
        {
          query: GetListingReportShareLinksDocument,
          variables: {
            input: {
              listingId: listing?.id,
              propertyDetailsId: propertyDetails?.id,
            },
          },
        },
      ],
    });
  };

  const onShare = async () => {
    if (!listing?.id && !propertyDetails?.id) {
      return;
    }

    setLoading(true);
    await sendListingReportShareLink({
      variables: {
        input: {
          listingId: listing?.id,
          propertyDetailsId: propertyDetails?.id,
          recipientEmail: email,
          recipientPhoneNumber: phoneNumber,
          message,
        },
      },
      refetchQueries: [
        {
          query: GetListingReportShareLinksDocument,
          variables: {
            input: {
              listingId: listing?.id,
              propertyDetailsId: propertyDetails?.id,
            },
          },
        },
      ],
      onCompleted: () => {
        toast({
          title: 'Listing Report shared',
          description: 'The Listing Report has been shared with the client',
          status: 'success',
        });
      },
      onError: () => {
        toast({
          title: 'Error sharing Listing Report',
          description: 'There was an error sharing the Listing Report',
          status: 'error',
        });
      },
    });
    setLoading(false);
    onClose();
  };

  const onPreview = () => {
    const url = `${location.pathname}/preview`;
    openInNewTab(url);
  };

  return (
    <ShareModal
      email={email}
      isOpen={isOpen}
      loading={loading}
      message={message}
      phoneNumber={phoneNumber}
      revokeShareLink={revokeShareLink}
      setEmail={setEmail}
      setMessage={setMessage}
      setPhoneNumber={setPhoneNumber}
      shareLinks={shareLinks}
      title={`Listing Report${
        listing?.mlsListing?.address?.full
          ? ` for ${listing?.mlsListing?.address?.full}`
          : propertyDetails?.streetAddress
            ? ` for ${propertyDetails?.streetAddress}${
                propertyDetails?.unit ? ` ${propertyDetails?.unit}` : ''
              }`
            : ''
      }`}
      onClose={onClose}
      onPreview={onPreview}
      onShare={onShare}
    />
  );
};
